import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, FileText, Clock, Edit, Settings, Trash2, BookOpen } from 'lucide-react';
import { useDashboard } from '@/contexts/DashboardContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import DashboardOverview from './DashboardOverview';
import { useToast } from '@/hooks/use-toast';
import { Article } from '@/integrations/supabase/customTypes';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const DashboardContent = () => {
  const { articles, stats, refreshDashboardData } = useDashboard();
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<"overview" | "articles">("overview");
  
  useEffect(() => {
    if (location.pathname === '/dashboard/content') {
      setActiveTab("articles");
    }
  }, [location.pathname]);

  const handleArticleAction = (articleId: string, action: string) => {
    if (action === 'edit') {
      navigate(`/dashboard/editor/${articleId}`);
    } else if (action === 'preview') {
      navigate(`/article/${articleId}`);
    } else if (action === 'settings') {
      toast({
        title: "Settings",
        description: "Navigating to article settings...",
      });
    } else if (action === 'delete') {
      toast({
        title: "Delete",
        description: "Deleting article...",
      });
    }
  };

  const renderArticlesTab = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Articles</CardTitle>
          <CardDescription>
            Manage and view your articles.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-auto">
          <table className="min-w-full divide-y divide-border">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Views
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {articles && articles.map(renderArticleRow)}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <Button onClick={refreshDashboardData}>Refresh Data</Button>
    </div>
  );

  const renderArticleRow = (article: Article) => {
    const formattedDate = article.updated_at
      ? format(new Date(article.updated_at), 'MMM d, yyyy')
      : 'N/A';

    return (
      <tr key={article.id} className="hover:bg-muted/50">
        <td className="py-3 px-4">
          <div className="flex flex-col">
            <span className="font-medium">{article.title || 'Untitled Article'}</span>
            {article.subtitle && (
              <span className="text-sm text-muted-foreground">{article.subtitle}</span>
            )}
          </div>
        </td>
        <td className="py-3 px-4">
          <Badge
            variant={article.status === 'published' ? 'default' : 'outline'}
            className={article.status === 'published' ? 'bg-green-500' : ''}
          >
            {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
          </Badge>
        </td>
        <td className="py-3 px-4">{formattedDate}</td>
        <td className="py-3 px-4 text-center">
          <div className="flex items-center justify-center gap-1">
            <Eye className="h-4 w-4 text-muted-foreground" />
            <span>{article.view_count || 0}</span>
          </div>
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleArticleAction(article.id, 'edit')}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleArticleAction(article.id, 'preview')}
            >
              <BookOpen className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleArticleAction(article.id, 'settings')}
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleArticleAction(article.id, 'delete')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  if (location.pathname === '/dashboard/content') {
    return (
      <div className="w-full">
        {renderArticlesTab()}
      </div>
    );
  }

  return (
    <div className="w-full">
      <Tabs defaultValue="overview" className="w-full" value={activeTab} onValueChange={(value) => setActiveTab(value as "overview" | "articles")}>
        <TabsList>
          <TabsTrigger value="overview" onClick={() => setActiveTab("overview")}>Overview</TabsTrigger>
          <TabsTrigger value="articles" onClick={() => setActiveTab("articles")}>Articles</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-6">
          <DashboardOverview stats={stats} />
        </TabsContent>
        <TabsContent value="articles" className="mt-6">
          {renderArticlesTab()}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DashboardContent;
